import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import { tokens } from 'styling/tokens'
import { AttendanceDonut } from './AttendanceDonut'
import { ShiftCoverageWidget } from './ShiftCoverageWidget'
import {
  BarRow,
  BarLabel,
  BarTrack,
  BarFill,
  BarValue,
  OccupancyValue,
  OccupancyCaption,
} from '../hrLayout.styled'
import { timeAgo } from 'utils/helpers'

const DEPARTMENTS = [
  { name: 'Mining Ops', present: 78, total: 85 },
  { name: 'Maintenance', present: 39, total: 42 },
  { name: 'Haulage', present: 30, total: 35 },
  { name: 'Processing', present: 27, total: 28 },
  { name: 'HSE', present: 14, total: 15 },
  { name: 'Admin', present: 10, total: 10 },
]

const MEALS = [
  { name: 'Breakfast', served: 172, total: 180, color: tokens.status.success.fg },
  { name: 'Lunch', served: 142, total: 195, color: tokens.accent.base },
  { name: 'Dinner', served: 0, total: 190, color: tokens.text.muted },
]

const TRANSPORT = [
  { label: 'Inbound', count: 78, total: 85, color: tokens.status.info.fg },
  { label: 'Outbound', count: 54, total: 72, color: tokens.status.success.fg },
  { label: 'North gate', count: 22, total: 30, color: tokens.accent.base },
]

const SUGGESTIONS = [
  'Move 2 staff from Admin to Haulage to balance load',
  'Approve 3 pending overtime requests before payroll',
  'Accommodation at 84% — pre-book 12 beds for next intake',
  'Renew 4 visas expiring this month',
]

export const HrWidgetBody = ({ type }: { type: string }) => {
  const { accommodationSummary } = useSelector(
    (state: RootState) => state.hrDashboard,
  )
  const { items } = useSelector((state: RootState) => state.notificationCenter)

  const accommodation = useMemo(() => {
    const a = accommodationSummary
    const total = a?.totalCapacity ?? 220
    const occupied = a?.occupied ?? 184
    const available2 = a?.available ?? total - occupied
    const rawRate = a?.occupancyRate
    const pct =
      rawRate != null
        ? Math.round(rawRate <= 1 ? rawRate * 100 : rawRate)
        : total
          ? Math.round((occupied / total) * 100)
          : 0
    return { total, occupied, available: available2, pct, dirty: a?.dirtyRooms ?? 0 }
  }, [accommodationSummary])

  const notifRows = useMemo(() => {
    const rank = (n: any) => (n.requiresAck && !n.acknowledged ? 0 : !n.read ? 1 : 2)
    return [...items]
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, 8)
      .map((n) => ({
        id: n.id,
        title: n.title,
        meta: `${n.category} · ${timeAgo(n.createdAt) || 'just now'}`,
        pillLabel: n.severity.toUpperCase(),
        pillTone: (n.severity === 'high' ? 'danger' : n.severity === 'med' ? 'warning' : 'info') as
          | 'danger'
          | 'warning'
          | 'info',
      }))
  }, [items])

  if (type === 'attendance') {
    return <AttendanceDonut />
  }
  if (type === 'department') {
    return (
      <>
        {DEPARTMENTS.map((d) => {
          const pct = Math.round((d.present / d.total) * 100)
          return (
            <BarRow key={d.name}>
              <BarLabel>{d.name}</BarLabel>
              <BarTrack>
                <BarFill $pct={pct} $color={pct >= 95 ? tokens.status.success.fg : undefined} />
              </BarTrack>
              <BarValue>{pct}%</BarValue>
            </BarRow>
          )
        })}
      </>
    )
  }
  if (type === 'accommodation') {
    return (
      <>
        <OccupancyValue>
          {accommodation.occupied} / {accommodation.total}
        </OccupancyValue>
        <OccupancyCaption>beds occupied · {accommodation.pct}% occupancy</OccupancyCaption>
        <BarTrack>
          <BarFill $pct={accommodation.pct} />
        </BarTrack>
        <OccupancyCaption>
          {accommodation.available} beds available · {accommodation.dirty} dirty rooms
        </OccupancyCaption>
      </>
    )
  }
  if (type === 'shifts') {
    return <ShiftCoverageWidget />
  }
  if (type === 'notifications' || type === 'alerts') {
    if (!notifRows.length) {
      return (
        <OccupancyCaption style={{ marginBottom: 0 }}>
          {type === 'alerts' ? 'No alerts' : 'No notifications'}
        </OccupancyCaption>
      )
    }
    return (
      <>
        {notifRows.map((n) => (
          <AlertListRow
            key={n.id}
            title={n.title}
            meta={n.meta}
            pillLabel={type === 'alerts' ? n.pillLabel : undefined}
            pillTone={n.pillTone}
          />
        ))}
      </>
    )
  }
  if (type === 'food') {
    return (
      <>
        {MEALS.map((m) => {
          const pct = m.total ? Math.round((m.served / m.total) * 100) : 0
          return (
            <BarRow key={m.name}>
              <BarLabel>{m.name}</BarLabel>
              <BarTrack>
                <BarFill $pct={pct} $color={m.color} />
              </BarTrack>
              <BarValue>{m.served}</BarValue>
            </BarRow>
          )
        })}
      </>
    )
  }
  if (type === 'transport') {
    return (
      <>
        {TRANSPORT.map((t) => {
          const pct = t.total ? Math.round((t.count / t.total) * 100) : 0
          return (
            <BarRow key={t.label}>
              <BarLabel>{t.label}</BarLabel>
              <BarTrack>
                <BarFill $pct={pct} $color={t.color} />
              </BarTrack>
              <BarValue>{t.count}</BarValue>
            </BarRow>
          )
        })}
      </>
    )
  }
  if (type === 'suggestions') {
    return (
      <>
        {SUGGESTIONS.map((s) => (
          <AlertListRow key={s} title={s} />
        ))}
      </>
    )
  }
  return null
}
